import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useQuoteCart } from '../context/QuoteCartContext';

const ASSETS = 'https://customer-assets-jai6qajn.emergentagent.net/job_cheratina-b2b/artifacts';
const PELLET_IMG = `${ASSETS}/bnc14p04_light%20blonde%20pellet.png`;
const HERO_COLORS = [
  { key: 'pellet-trans',  label: 'Pellet · Transparent', img: `${ASSETS}/phaabpjq_transparent%20pellet.png` },
  { key: 'pellet-blonde', label: 'Pellet · Blonde',      img: `${ASSETS}/bnc14p04_light%20blonde%20pellet.png` },
  { key: 'powder-black',  label: 'Powder · Black',       img: `${ASSETS}/4jfe44sp_keratin%20black%20powder%202-5%20microns.png` },
  { key: 'roll-brown',    label: 'Roll · Brown',         img: `${ASSETS}/ebso8vu7_keratin%20strip%20in%20roll%20brown.png` },
];
const CRAFT_IMG = `${ASSETS}/phaabpjq_transparent%20pellet.png`;
const SALON_IMG = 'https://images.pexels.com/photos/27165072/pexels-photo-27165072.jpeg';

function getPName(p, lang) {
  return p.translations?.[lang]?.name || p.translations?.en?.name || p.translations?.it?.name || p.sku;
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const { setOpen } = useQuoteCart();

  useEffect(() => {
    api.get('/products')
      .then(r => setFeatured(r.data.slice(0, 8)))
      .catch(err => logger.warn('[HomePage] Failed to load featured products:', err?.message));
  }, []);

  return (
    <div>
      {/* HERO — text left, pellets image right, no strip below */}
      <section className="relative overflow-hidden bg-[#3D2817] min-h-[92vh]">
        {/* Subtle decorative gradient */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-[#3D2817] via-[#2A1B10] to-[#3D2817]" />
          <div className="absolute -top-1/3 -right-1/4 w-[60vw] h-[60vw] rounded-full bg-[#C5A059]/10 blur-3xl" />
          <div className="absolute -bottom-1/3 -left-1/4 w-[60vw] h-[60vw] rounded-full bg-[#C5A059]/5 blur-3xl" />
        </div>

        {/* Top brand banner — big COSMETISSAGE logo in gold, centered */}
        <div className="absolute top-8 md:top-12 left-0 right-0 z-10 text-center fade-up" data-testid="hero-brand-banner">
          <div className="inline-block">
            <h2 className="font-serif font-light text-4xl sm:text-6xl md:text-7xl lg:text-8xl tracking-[0.15em] md:tracking-[0.25em] text-[#C5A059] uppercase leading-none">
              COSMETISSAGE<sup className="text-[0.35em] align-super text-[#C5A059]/70 ml-1">®</sup>
            </h2>
            <div className="mx-auto mt-4 w-24 h-px bg-[#C5A059]/50"></div>
            <div className="text-xs uppercase tracking-[0.5em] text-[#C5A059] mt-4">Hair & Beauty</div>
          </div>
        </div>

        {/* Two-column content */}
        <div className="relative max-w-[1440px] w-full mx-auto px-6 md:px-12 py-24 md:py-28 min-h-[92vh] grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center text-white pt-40 md:pt-48">
          {/* LEFT: Text */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6 fade-up" data-testid="hero-brand-eyebrow">— {t('hero.eyebrow')}</div>
            <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight uppercase fade-up" style={{ animationDelay: '0.1s' }}>
              {t('hero.title_1')} <br />
              <em className="text-[#C5A059] not-italic font-medium">{t('hero.title_2')}</em> <br />
              <span className="font-light">{t('hero.title_3')}</span>
            </h1>
            <p className="mt-6 font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/70 fade-up" style={{ animationDelay: '0.25s' }}>
              — {t('hero.industry')}
            </p>
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-white/80 max-w-2xl fade-up" style={{ animationDelay: '0.4s' }}>
              {t('hero.subtitle')}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 fade-up" style={{ animationDelay: '0.55s' }}>
              <Link
                to="/catalog"
                data-testid="hero-cta-catalog"
                className="group inline-flex items-center gap-3 bg-[#C5A059] hover:bg-white hover:text-[#3D2817] text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-500 hover:-translate-y-0.5"
              >
                {t('hero.cta_catalog')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                data-testid="hero-cta-quote"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-3 border border-white/40 hover:border-white text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-500 hover:-translate-y-0.5"
              >
                {t('hero.cta_quote')}
              </button>
            </div>
          </div>

          {/* RIGHT: Pellets in 4 colors */}
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: '0.35s' }}>
            <div className="relative max-w-[520px] mx-auto">
              <div className="absolute -inset-8 rounded-3xl bg-[#C5A059]/15 blur-3xl" />
              <div className="relative grid grid-cols-2 gap-5 md:gap-6">
                {HERO_COLORS.map((c, i) => (
                  <div
                    key={c.key}
                    data-testid={`hero-pellet-${c.key}`}
                    className="group relative"
                    style={{ animation: `fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) ${0.15 + i * 0.1}s both` }}
                  >
                    <div className="aspect-square rounded-full overflow-hidden ring-1 ring-[#C5A059]/30 bg-[#2A1B10] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-1">
                      <img
                        src={c.img}
                        alt={`Keratin — ${c.label}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-3 text-center text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">{c.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — social proof strip below hero */}
      <section className="bg-[#F2ECE0] border-y border-[#E5D9C8]" data-testid="testimonials-strip">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            {/* LEFT: eyebrow + heading */}
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('testimonials.eyebrow')}</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3D2817]">
                {t('testimonials.title_1')}<br />
                <em className="text-[#C5A059] not-italic">{t('testimonials.title_2')}</em>
              </h2>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={`rating-star-${i}`} className="w-5 h-5 text-[#C5A059] fill-current" viewBox="0 0 20 20">
                      <path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
                    </svg>
                  ))}
                </div>
                <span className="font-serif italic text-sm text-[#3D2817]/70">{t('testimonials.verified')}</span>
              </div>
            </div>

            {/* RIGHT: 3 testimonials — asymmetric grid */}
            <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {[
                {
                  quote: "Received our keratin glue shipment from Cosmetissage — the quality is truly excellent. Looking forward to a long-term partnership with the team.",
                  name: "Sritanu Jana",
                  role: "Owner",
                  business: "JANA HUMAN HAIR",
                  country: "India",
                  flag: "🇮🇳",
                  initials: "SJ",
                  featured: true,
                },
                {
                  quote: "Excellent keratin glue quality and consistently reliable service over the years. Truly satisfied — professional team all around.",
                  name: "Deepak",
                  role: "Long-standing Client",
                  business: "Indi Tress Pvt Ltd",
                  country: "India",
                  flag: "🇮🇳",
                  initials: "DK",
                },
                {
                  quote: "We use Cosmetissage on every hair order — no quality complaints from our customers, batch after batch.",
                  name: "Chen",
                  role: "Top Client",
                  business: "NICE REMY HAIR INTERNATIONAL",
                  country: "China",
                  flag: "🇨🇳",
                  initials: "CH",
                },
              ].map((tst, i) => (
                <figure
                  key={tst.name}
                  data-testid={`testimonial-${i}`}
                  className={`relative bg-white p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(61,40,23,0.25)] border border-[#E5D9C8] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(197,160,89,0.4)] ${tst.featured ? 'md:col-span-2 md:row-span-1' : ''}`}
                  style={{ animation: `fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) ${i * 0.12}s both` }}
                >
                  {/* Big decorative quote mark */}
                  <div className="absolute top-4 right-6 font-serif text-[80px] leading-none text-[#C5A059]/15 select-none pointer-events-none">"</div>

                  <blockquote className={`relative font-serif italic leading-[1.5] text-[#3D2817] ${tst.featured ? 'text-xl md:text-2xl' : 'text-base md:text-lg'}`}>
                    {tst.quote}
                  </blockquote>

                  <figcaption className="mt-6 pt-5 border-t border-[#E5D9C8] flex items-center gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#3D2817] text-[#C5A059] flex items-center justify-center font-serif text-sm tracking-widest">
                      {tst.initials}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#3D2817] text-sm">{tst.name}</div>
                      <div className="text-xs text-[#3D2817]/60 truncate">
                        {tst.role} · {tst.business}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mt-0.5 flex items-center gap-1.5">
                        <span className="text-sm leading-none">{tst.flag}</span>
                        <span>{tst.country}</span>
                      </div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">— {t('catalog.title')}</div>
            <h2 className="font-serif font-light text-5xl md:text-6xl leading-none">{t('catalog.title')}</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="font-serif italic text-xl text-[#3D2817]/70">{t('catalog.subtitle')}</p>
          </div>
        </div>

        {featured.length === 0 ? (
          <div className="text-center py-16 text-[#3D2817]/50 font-serif italic text-xl">
            The catalog will appear here soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featured.map((p, i) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`featured-product-${p.id}`}
                className="group block"
                style={{ animation: `fadeUp 0.8s cubic-bezier(0.22,1,0.36,1) ${i * 0.1}s both` }}
              >
                <div className="img-zoom aspect-[3/4] bg-[#F2ECE0] mb-4">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={getPName(p, i18n.language)} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#C5A059] font-serif text-4xl italic">K.</div>
                  )}
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#3D2817]/50">{p.sku}</div>
                    <div className="font-serif text-2xl leading-tight">{getPName(p, i18n.language)}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* BULK ORDERS · Packaging & Wholesale */}
      <section className="bg-[#3D2817] text-white py-24 md:py-32 relative overflow-hidden" data-testid="bulk-orders">
        {/* Subtle gold gradient wash */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-1/3 -right-1/4 w-[50vw] h-[50vw] rounded-full bg-[#C5A059]/8 blur-3xl" />
          <div className="absolute -bottom-1/3 -left-1/4 w-[45vw] h-[45vw] rounded-full bg-[#C5A059]/6 blur-3xl" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* LEFT — heading + showroom image + CTA */}
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('bulk.eyebrow')}</div>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[1.02] mb-6">
                {t('bulk.title_1')} <br />
                <em className="text-[#C5A059] not-italic">{t('bulk.title_2')}</em>
              </h2>
              <p className="font-serif italic text-lg text-white/70 leading-relaxed mb-8 max-w-md">
                {t('bulk.subtitle')}
              </p>

              {/* Milan showroom image */}
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm mb-8 ring-1 ring-[#C5A059]/20">
                <img
                  src={`${ASSETS}/jaua37gk_reception%20showroom%20%2B%20commercial%20office.png`}
                  alt="COSMETISSAGE Milan showroom · Bulk order fulfilment"
                  className="w-full h-full object-cover"
                  data-testid="bulk-showroom-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3D2817]/70 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-5 right-5">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">— {t('bulk.milan_hq')}</div>
                  <div className="font-serif italic text-lg">{t('bulk.milan_shipping')}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setOpen(true)}
                  data-testid="bulk-cta-quote"
                  className="group inline-flex items-center gap-3 bg-[#C5A059] hover:bg-white hover:text-[#3D2817] text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-500 hover:-translate-y-0.5"
                >
                  {t('bulk.cta_quote')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  to="/private-label"
                  data-testid="bulk-cta-oem"
                  className="group inline-flex items-center gap-3 border border-white/40 hover:border-white text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-500 hover:-translate-y-0.5"
                >
                  {t('bulk.cta_oem')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* RIGHT — Real packaging photos + size chips */}
            <div className="lg:col-span-7">
              {/* Two real packaging photos side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {[
                  {
                    img: `${ASSETS}/iyez175v_new%20packaging%2020kg%20for%20transparent%20color.png`,
                    label: 'Transparent · 20 kg',
                    testid: 'bulk-photo-20kg',
                  },
                  {
                    img: `${ASSETS}/fcmpr20l_new%20packaging%2C%20more%20protection.png`,
                    label: 'Dark Blonde · 25 kg',
                    testid: 'bulk-photo-25kg',
                  },
                ].map((p, i) => (
                  <div
                    key={p.testid}
                    data-testid={p.testid}
                    className="group relative bg-white/95 border border-[#C5A059]/25 hover:border-[#C5A059] transition-all duration-500 hover:-translate-y-1 overflow-hidden"
                    style={{ animation: `fadeUp 0.8s cubic-bezier(0.22,1,0.36,1) ${i * 0.12}s both` }}
                  >
                    <div className="aspect-[4/5] bg-[#F2ECE0] overflow-hidden">
                      <img
                        src={p.img}
                        alt={`COSMETISSAGE keratin packaging — ${p.label}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-4 md:p-5 text-center bg-white">
                      <div className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059] mb-1">— Real Packaging</div>
                      <div className="font-serif text-lg text-[#3D2817]">{p.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Real pellet color palette — 6 colors available */}
              <div className="mt-6 grid grid-cols-6 gap-2 md:gap-3">
                {[
                  { color: 'Transparent',  img: `${ASSETS}/phaabpjq_transparent%20pellet.png` },
                  { color: 'Blonde',       img: `${ASSETS}/bnc14p04_light%20blonde%20pellet.png` },
                  { color: 'Dark Blonde',  img: `${ASSETS}/g8a4ifin_dark%20blonde.png` },
                  { color: 'Brown',        img: `${ASSETS}/vw6ppnzj_brown%20pellet.png` },
                  { color: 'Dark Brown',   img: `${ASSETS}/11gqqqs1_dark%20brown%20pellet.png` },
                  { color: 'Black',        img: `${ASSETS}/llhwhl8z_black%20pellets.png` },
                ].map((c, i) => (
                  <div
                    key={c.color}
                    data-testid={`bulk-pellet-${c.color.toLowerCase().replace(' ', '-')}`}
                    className="group text-center"
                    style={{ animation: `fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) ${0.2 + i * 0.05}s both` }}
                  >
                    <div className="aspect-square overflow-hidden ring-1 ring-[#C5A059]/30 group-hover:ring-[#C5A059] transition-all bg-[#F2ECE0]">
                      <img
                        src={c.img}
                        alt={`Keratin pellets — ${c.color}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="mt-2 text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">{c.color}</div>
                  </div>
                ))}
              </div>

              {/* Size availability chips */}
              <div className="mt-8 p-6 border border-[#C5A059]/25 bg-[#2A1B10]/40">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('bulk.available_formats')}</div>
                <div className="grid grid-cols-4 gap-3 md:gap-4">
                  {[
                    { weight: '5',  target: t('bulk.target_sample') },
                    { weight: '10', target: t('bulk.target_small') },
                    { weight: '20', target: t('bulk.target_standard') },
                    { weight: '25', target: t('bulk.target_industrial') },
                  ].map((s, i) => (
                    <div
                      key={s.weight}
                      data-testid={`bulk-size-${s.weight}kg`}
                      className="group text-center border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059]/10 transition-all duration-300 py-4 cursor-default"
                      style={{ animation: `fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) ${0.3 + i * 0.06}s both` }}
                    >
                      <div className="flex items-baseline justify-center gap-0.5 mb-1">
                        <span className="font-serif font-light text-3xl md:text-4xl text-[#C5A059] leading-none">{s.weight}</span>
                        <span className="font-serif italic text-xs text-white/60">kg</span>
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.25em] text-white/60">{s.target}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features strip below packages */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-[#C5A059]/20">
                {[
                  { t: t('bulk.feature_aluminum'), d: t('bulk.feature_aluminum_desc') },
                  { t: t('bulk.feature_custom'),   d: t('bulk.feature_custom_desc') },
                  { t: t('bulk.feature_label'),    d: t('bulk.feature_label_desc') },
                ].map(f => (
                  <div key={f.t} data-testid={`bulk-feature-${f.t.split(' ')[0].toLowerCase()}`}>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-1">— {f.t}</div>
                    <div className="text-sm text-white/70">{f.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY COSMETISSAGE */}
      <section className="bg-[#FBF8F3] py-24 md:py-32" data-testid="why-cosmetissage">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('why.eyebrow')}</div>
              <h2 className="font-serif text-5xl md:text-6xl font-light leading-none">
                {t('why.title_1')} <br />
                <em className="text-[#C5A059] not-italic font-medium">COSMETISSAGE<sup className="text-[0.6em]">®</sup></em>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="font-serif italic text-lg text-[#3D2817]/70">
                {t('why.intro')}
              </p>
            </div>
          </div>

          {/* Benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-24">
            {[
              { n: '01', title: t('why.b01_t'), body: t('why.b01_b') },
              { n: '02', title: t('why.b02_t'), body: t('why.b02_b') },
              { n: '03', title: t('why.b03_t'), body: t('why.b03_b') },
              { n: '04', title: t('why.b04_t'), body: t('why.b04_b') },
              { n: '05', title: t('why.b05_t'), body: t('why.b05_b') },
              { n: '06', title: t('why.b06_t'), body: t('why.b06_b') },
              { n: '07', title: t('why.b07_t'), body: t('why.b07_b') },
              { n: '08', title: t('why.b08_t'), body: t('why.b08_b') },
            ].map((b, i) => (
              <div key={b.n} data-testid={`why-benefit-${b.n}`} className="group border-t border-[#3D2817]/15 pt-6 hover:border-[#C5A059] transition-colors" style={{ animation: `fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 0.06}s both` }}>
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-3">{b.n}</div>
                <h3 className="font-serif text-2xl leading-tight mb-3 group-hover:text-[#C5A059] transition-colors">{b.title}</h3>
                <p className="text-sm text-[#3D2817]/70 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>

          {/* Process pipeline */}
          <div className="mb-24" data-testid="why-process">
            <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('why.process_eyebrow')}</div>
            <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-10">
              {t('why.process_title_1')}<em className="text-[#C5A059] not-italic font-medium">{t('why.process_title_2')}</em>.
            </h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-6">
              {[
                t('why.s1'),
                t('why.s2'),
                t('why.s3'),
                t('why.s4'),
                t('why.s5'),
                t('why.s6'),
              ].map((step, i, arr) => (
                <div key={step} className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#C5A059] text-[#C5A059] font-serif text-sm">{i + 1}</span>
                    <span className="font-serif text-lg md:text-xl">{step}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <span className="text-[#C5A059] text-2xl leading-none hidden sm:inline">→</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Worldwide reach */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center" data-testid="why-worldwide">
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('worldwide.eyebrow')}</div>
              <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-6">
                {t('worldwide.title_1')} <br />
                <em className="text-[#C5A059] not-italic font-medium">{t('worldwide.title_2')}</em>
              </h3>
              <p className="font-serif italic text-lg text-[#3D2817]/70 mb-8">
                {t('worldwide.subtitle')}
              </p>
              <div className="space-y-2.5">
                {[
                  { flag: '🇨🇳', name: 'China',    desc: t('worldwide.market_china') },
                  { flag: '🇮🇳', name: 'India',    desc: t('worldwide.market_india') },
                  { flag: '🇻🇳', name: 'Vietnam',  desc: t('worldwide.market_vietnam') },
                  { flag: '🇪🇺', name: 'Europe',   desc: t('worldwide.market_europe') },
                  { flag: '🌎', name: 'Americas', desc: t('worldwide.market_americas') },
                  { flag: '🌍', name: 'MENA',     desc: t('worldwide.market_mena') },
                ].map(c => (
                  <div key={c.name} className="flex items-center gap-3 pb-2 border-b border-[#3D2817]/10">
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="text-sm font-semibold text-[#3D2817] min-w-[70px]">{c.name}</span>
                    <span className="text-xs text-[#3D2817]/50 italic">{c.desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="relative aspect-[16/9] bg-gradient-to-br from-[#3D2817] via-[#4a3020] to-[#3D2817] overflow-hidden rounded-sm shadow-[0_25px_60px_-20px_rgba(61,40,23,0.5)]">
                {/* Decorative starfield dots */}
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage: 'radial-gradient(#C5A059 1px, transparent 1px)',
                    backgroundSize: '22px 22px',
                  }}
                />
                <div className="absolute top-5 left-6 text-[10px] uppercase tracking-[0.35em] text-[#C5A059]/60">— Shipping map</div>
                <div className="absolute bottom-5 right-6 text-[10px] uppercase tracking-[0.35em] text-[#C5A059]/60">Milan · HQ</div>

                <svg
                  viewBox="0 0 1000 500"
                  className="absolute inset-0 w-full h-full"
                  fill="none"
                  aria-label="World shipping map from Milan"
                  data-testid="worldmap-svg"
                >
                  <defs>
                    {/* Golden gradient for arrows */}
                    <linearGradient id="arrowGold" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#C5A059" stopOpacity="0" />
                      <stop offset="30%" stopColor="#C5A059" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#F0D89A" stopOpacity="1" />
                    </linearGradient>
                    {/* Milan glow radial */}
                    <radialGradient id="milanGlow">
                      <stop offset="0%" stopColor="#F0D89A" stopOpacity="0.9" />
                      <stop offset="60%" stopColor="#C5A059" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
                    </radialGradient>
                    {/* Arrowhead marker */}
                    <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
                      <path d="M0,0 L10,5 L0,10 L3,5 Z" fill="#F0D89A" />
                    </marker>
                  </defs>

                  {/* --- Simplified continents (dotted, muted) --- */}
                  <g fill="#C5A059" opacity="0.15">
                    {/* North America */}
                    <path d="M 90 130 Q 100 100 130 105 L 200 100 Q 240 108 260 130 L 275 165 Q 270 195 250 210 L 220 220 Q 180 218 150 200 L 120 180 Q 95 155 90 130 Z" />
                    {/* Central America */}
                    <path d="M 220 220 Q 235 230 240 250 L 235 270 Q 225 275 215 265 Z" />
                    {/* South America */}
                    <path d="M 260 260 Q 290 255 300 285 L 305 340 Q 295 380 275 400 L 255 405 Q 245 385 250 350 L 255 300 Z" />
                    {/* Europe */}
                    <path d="M 480 130 Q 500 120 525 125 L 555 135 Q 560 155 545 165 L 510 170 Q 485 160 478 145 Z" />
                    {/* Africa */}
                    <path d="M 510 200 Q 550 200 570 220 L 590 275 Q 585 320 560 355 L 535 370 Q 510 345 505 300 L 500 240 Z" />
                    {/* Middle East */}
                    <path d="M 570 195 Q 600 190 620 205 L 625 235 Q 605 245 580 230 Z" />
                    {/* Russia */}
                    <path d="M 555 90 L 720 85 Q 790 90 850 110 L 850 145 Q 800 145 720 140 L 605 138 Q 570 130 555 115 Z" />
                    {/* China / Asia */}
                    <path d="M 700 145 Q 750 140 800 155 L 830 195 Q 810 225 780 235 L 740 235 Q 705 220 690 195 Z" />
                    {/* India */}
                    <path d="M 700 220 Q 720 220 730 240 L 725 275 Q 705 285 690 265 Z" />
                    {/* Southeast Asia */}
                    <path d="M 790 250 Q 815 245 825 265 L 820 285 Q 800 290 785 275 Z" />
                    {/* Australia */}
                    <path d="M 830 340 Q 875 335 895 355 L 895 380 Q 870 390 840 385 L 820 365 Z" />
                  </g>

                  {/* Continent outlines (subtle) */}
                  <g fill="none" stroke="#C5A059" strokeWidth="0.8" opacity="0.35" strokeLinejoin="round">
                    <path d="M 90 130 Q 100 100 130 105 L 200 100 Q 240 108 260 130 L 275 165 Q 270 195 250 210 L 220 220 Q 180 218 150 200 L 120 180 Q 95 155 90 130 Z" />
                    <path d="M 260 260 Q 290 255 300 285 L 305 340 Q 295 380 275 400 L 255 405 Q 245 385 250 350 L 255 300 Z" />
                    <path d="M 480 130 Q 500 120 525 125 L 555 135 Q 560 155 545 165 L 510 170 Q 485 160 478 145 Z" />
                    <path d="M 510 200 Q 550 200 570 220 L 590 275 Q 585 320 560 355 L 535 370 Q 510 345 505 300 L 500 240 Z" />
                    <path d="M 555 90 L 720 85 Q 790 90 850 110 L 850 145 Q 800 145 720 140 L 605 138 Q 570 130 555 115 Z" />
                    <path d="M 700 145 Q 750 140 800 155 L 830 195 Q 810 225 780 235 L 740 235 Q 705 220 690 195 Z" />
                    <path d="M 700 220 Q 720 220 730 240 L 725 275 Q 705 285 690 265 Z" />
                    <path d="M 790 250 Q 815 245 825 265 L 820 285 Q 800 290 785 275 Z" />
                    <path d="M 830 340 Q 875 335 895 355 L 895 380 Q 870 390 840 385 L 820 365 Z" />
                  </g>

                  {/* Milan glow */}
                  <circle cx="520" cy="155" r="35" fill="url(#milanGlow)" />

                  {/* --- Animated arrows from Milan to key markets --- */}
                  {/* Format: {label, endX, endY, dashDelay} */}
                  {[
                    { end: [760, 175], label: 'China',    delay: 0    },
                    { end: [710, 235], label: 'India',    delay: 0.35 },
                    { end: [795, 265], label: 'Vietnam',  delay: 0.7  },
                    { end: [500, 145], label: 'Europe',   delay: 1.05 },
                    { end: [235, 195], label: 'Americas', delay: 1.4  },
                    { end: [595, 240], label: 'MENA',     delay: 1.75 },
                  ].map((route, i) => {
                    const [ex, ey] = route.end;
                    const midX = (520 + ex) / 2;
                    const midY = (155 + ey) / 2 - Math.abs(ex - 520) * 0.15;
                    const path = `M 520 155 Q ${midX} ${midY} ${ex} ${ey}`;
                    return (
                      <g key={route.label}>
                        {/* Path — soft dashed */}
                        <path
                          d={path}
                          stroke="url(#arrowGold)"
                          strokeWidth="1.5"
                          strokeDasharray="4 4"
                          strokeLinecap="round"
                          markerEnd="url(#arrowhead)"
                          opacity="0.85"
                        >
                          <animate attributeName="stroke-dashoffset" from="0" to="-32" dur="2.2s" repeatCount="indefinite" begin={`${route.delay}s`} />
                        </path>
                        {/* Destination dot pulsing */}
                        <circle cx={ex} cy={ey} r="4" fill="#F0D89A">
                          <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" begin={`${route.delay}s`} />
                          <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" begin={`${route.delay}s`} />
                        </circle>
                        {/* Destination label */}
                        <text x={ex} y={ey + 18} textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="10" fill="#F0D89A" letterSpacing="1.5" fontWeight="600">
                          {route.label.toUpperCase()}
                        </text>
                      </g>
                    );
                  })}

                  {/* Milan HQ marker (drawn on top) */}
                  <g>
                    <circle cx="520" cy="155" r="8" fill="#F0D89A" stroke="#3D2817" strokeWidth="1.5" />
                    <circle cx="520" cy="155" r="4" fill="#3D2817" />
                    {/* Ring pulse */}
                    <circle cx="520" cy="155" r="8" fill="none" stroke="#F0D89A" strokeWidth="1.5">
                      <animate attributeName="r" values="8;22;8" dur="2.8s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="1;0;1" dur="2.8s" repeatCount="indefinite" />
                    </circle>
                    <text x="520" y="140" textAnchor="middle" fontFamily="Georgia, serif" fontSize="13" fill="#ffffff" fontStyle="italic">
                      Milano
                    </text>
                    <text x="520" y="128" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="8" fill="#F0D89A" letterSpacing="2" fontWeight="600">
                      HQ
                    </text>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MADE IN ITALY BAND */}
      <section className="bg-[#3D2817] text-white overflow-hidden">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2">
          <div className="p-12 md:p-24 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6" data-testid="brand-craft-eyebrow">— {t('craft.eyebrow')} · COSMETISSAGE<sup className="text-[0.6em]">®</sup></div>
            <h2 className="font-serif text-5xl md:text-6xl font-light leading-none mb-8">
              {t('craft.title_1')} <br />
              <em className="text-[#C5A059] not-italic font-medium">{t('craft.title_2')}</em>
            </h2>
            <p className="font-serif italic text-lg text-white/70 max-w-lg">
              {t('craft.body')}
            </p>
            <p className="mt-6 text-[11px] uppercase tracking-[0.35em] text-[#C5A059]/80" data-testid="brand-mark-craft">
              A registered brand by <span className="text-white font-semibold tracking-[0.4em]">COSMETISSAGE<sup className="text-[0.6em]">®</sup></span>
            </p>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.25em] text-[#C5A059] hover:text-white transition-colors"
            >
              {t('nav.about')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="img-zoom min-h-[500px]">
            <img src={CRAFT_IMG} alt="Italian craft" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="img-zoom aspect-[4/5]">
          <img src={SALON_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-6">— {t('partnership.eyebrow')}</div>
          <h2 className="font-serif text-5xl md:text-6xl font-light leading-none mb-8">
            {t('partnership.title_1')} <br /> <em className="text-[#C5A059] not-italic">{t('partnership.title_2')}</em>
          </h2>
          <p className="text-lg text-[#3D2817]/70 max-w-md">
            {t('partnership.body')}
          </p>
          <button
            data-testid="home-quote-cta"
            onClick={() => setOpen(true)}
            className="mt-10 self-start inline-flex items-center gap-3 bg-[#3D2817] hover:bg-[#C5A059] text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all duration-500"
          >
            {t('hero.cta_quote')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
